import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { CardModule } from 'primeng/card';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { debounceTime, fromEvent, interval, map, Subscription } from 'rxjs';
import { AuctionService } from '../../services/auction.service';
import { AuctionModel, Product, ProductItem } from '../../models/auction.model';
import { PriceFormatPipe } from '../../helpers/pipes/price-format';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { RippleModule } from 'primeng/ripple';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { GalleriaModule } from 'primeng/galleria';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { InfoButtonComponent } from '../../components/info-button/info-button.component';
import { SessionTokenService } from '../../services/session-token.service';

@Component({
  selector: 'app-auction-page',
  imports: [
    CommonModule,
    CardModule,
    ButtonModule,
    PriceFormatPipe,
    ToastModule,
    RippleModule,
    DynamicDialogModule,
    DialogModule,
    GalleriaModule,
    InfoButtonComponent,
  ],
  templateUrl: './auction-page.component.html',
  styleUrl: './auction-page.component.scss',
})
export class AuctionPageComponent implements OnInit, OnDestroy {
  private token = inject(SessionTokenService);
  activeProduct: 'first' | 'second' | null = null;
  auctionData = signal<AuctionModel>({} as AuctionModel);
  secondsLeft = signal<number>(-1);
  firstProductErrorReserved = signal<boolean>(false);
  firstProductErrorMessage = signal<string>('');
  secondProductErrorReserved = signal<boolean>(false);
  secondProductErrorMessage = signal<string>('');
  firstProductReserved = signal<boolean>(false);
  secondProductReserved = signal<boolean>(false);
  firstProductActuallyStep = signal<number>(0);
  secondProductActuallyStep = signal<number>(0);
  fireScaleFirst = signal(1);
  fireScaleSecond = signal(1);
  fireScaleAuction = signal(1);
  newFirstProduct = signal<string>('');
  newSecondProduct = signal<string>('');
  private auctionEndAt = signal<number | null>(null);

  firstImages: Array<string | { src: string; thumb?: string; alt?: string }> =
    [];
  secondImages: Array<string | { src: string; thumb?: string; alt?: string }> =
    [];

  activeIndexFirst = 0;
  activeIndexSecond = 0;
  isMobile = false;
  isTablet = false;
  isDesktop = false;
  menuOpen = false;
  startX = 0;
  startY = 0;
  slideLeft = false;
  slideRight = false;
  reservedPositionFirst = signal<number | null>(null);
  reservedPositionSecond = signal<number | null>(null);

  maxStockFirst = signal<number | null>(null);
  maxStockSecond = signal<number | null>(null);

  normalizeImg(img: any): { src: string; alt: string } {
    if (!img) return { src: '', alt: '' };

    if (typeof img === 'string') {
      return { src: img, alt: '' };
    }

    if (img.itemImageSrc) {
      return { src: img.itemImageSrc, alt: img.alt || '' };
    }

    return { src: img.src || img.url || '', alt: img.alt || '' };
  }

  private auctionService = inject(AuctionService);
  private messageService = inject(MessageService);
  private route = inject(ActivatedRoute);
  private router: Router = inject(Router);
  auctionId = toSignal(this.route.paramMap.pipe(map((p) => p.get('id')!)));
  private lastFirstItemLevel = 0;
  private lastSecondItemLevel = 0;
  private prevFirstStep = -1;
  private prevSecondStep = -1;
  private prevAuctionStep = -1;
  private mercureSub!: Subscription;
  private timerSub!: Subscription;
  private pollSub?: Subscription;

  private swapOrder = signal(false);

  timeUnits = computed(() => {
    const total = this.secondsLeft();
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);

    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;

    return [
      {
        value: pad(minutes),
        raw: minutes,
      },
      {
        value: pad(seconds),
        raw: seconds,
      },
    ];
  });

  constructor(private breakpointObserver: BreakpointObserver) {
    this.breakpointObserver
      .observe(['(max-width: 768px)', '(max-width: 1024px)'])
      .subscribe((result) => {
        if (result.breakpoints['(max-width: 768px)']) {
          this.isMobile = true;
          this.isTablet = false;
          this.isDesktop = false;
        } else if (result.breakpoints['(max-width: 1200px)']) {
          this.isTablet = true;
          this.isMobile = false;
          this.isDesktop = false;
        } else {
          this.isDesktop = true;
          this.isTablet = false;
          this.isMobile = false;
        }
      });

    effect(() => {
      const id = this.auctionId();

      if (!id) return;

      this.subscribeToAuction(id);
    });
  }

  private subscribeToAuction(id: string) {
    this.mercureSub?.unsubscribe();
    this.timerSub?.unsubscribe();
    this.pollSub?.unsubscribe();

    this.auctionService.getAuctionDetails(id).subscribe({
      next: (data: any) => {
        this.auctionEndAt.set(new Date(data.endAt).getTime());
        this.firstImages = data.firstItem.product.images.map(
          (img: any) => img.url
        );
        this.secondImages = data.secondItem.product.images.map(
          (img: any) => img.url
        );
        const currentFirstLevel = data.firstItem.data?.level;
        const currentSecondLevel = data.secondItem.data?.level;
        if (currentFirstLevel > this.lastFirstItemLevel) {
          this.activeProduct = 'first';
        } else if (currentSecondLevel > this.lastSecondItemLevel) {
          this.activeProduct = 'second';
        }
        this.lastFirstItemLevel = currentFirstLevel;
        this.lastSecondItemLevel = currentSecondLevel;
        this.auctionData.set(data);
        this.startCountdownFromEndAt();
        // this.startCountdown(data.timeLeft);
      },
    });

    this.pollSub = interval(5000).subscribe(() => {
      if (this.auctionData()?.state === 'ongoing') {
        // this.syncTimer(this.auctionData());
        this.auctionService.getAuctionInfo(id).subscribe();
      }
    });

    this.mercureSub = this.auctionService
      .auctionStream(id)
      .subscribe((data) => {
        this.handleMercureUpdate(data);
      });
  }

  // private syncTimer(auction: AuctionModel) {
  //   const serverTimeLeft = auction.timeLeft;
  //   if (Math.abs(this.secondsLeft() - serverTimeLeft) >= 5) {
  //     // this.startCountdown(serverTimeLeft);
  //   }
  // }

  handleMercureUpdate(data: AuctionModel) {
    if (data?.id) {
      this.firstImages = data.firstItem.product.images.map((img) => img.url);
      this.secondImages = data.secondItem.product.images.map((img) => img.url);
      const currentFirstLevel = data.firstItem.data?.level;
      const currentSecondLevel = data.secondItem.data?.level;
      if (currentFirstLevel > this.lastFirstItemLevel) {
        this.activeProduct = 'first';
      } else if (currentSecondLevel > this.lastSecondItemLevel) {
        this.activeProduct = 'second';
      }
      this.lastFirstItemLevel = currentFirstLevel;
      this.lastSecondItemLevel = currentSecondLevel;
      this.auctionData.set(data);
      // this.startCountdown(data.timeLeft);
    }
    if (data?.state === 'finished') {
      // this.startCountdown(0);
      this.mercureSub?.unsubscribe();
      this.pollSub?.unsubscribe();
      this.timerSub?.unsubscribe();
      this.auctionService.stopStream();
      if (this.token.token) {
        this.auctionService.firstProductReservations.set(0);
        this.auctionService.secondProductReservations.set(0);
        this.router.navigate(['/auction-list']);
      } else {
        this.goToSummaryPage(this.auctionData());
      }
      return;
    }
  }

  private onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.resyncFromServer();
    }
  };

  private resyncFromServer() {
    const id = this.auctionId();
    if (!id) return;

    this.auctionService.getAuctionDetails(id).subscribe((data) => {
      this.auctionData.set(data);

      this.auctionEndAt.set(new Date(data.endAt).getTime());

      this.startCountdownFromEndAt();

      if (data.state === 'finished') {
        this.onAuctionFinished();
      }
    });
  }

  getGalleryStyle() {
    if (this.isDesktop) {
      return {
        width: '100%',
        height: '100%',
        maxWidth: '600px',
        maxHeight: '400px',
        border: 'none',
      };
    } else if (this.isTablet) {
      return {
        width: '100%',
        height: '100%',
        maxWidth: '500px',
        maxHeight: '300px',
        border: 'none',
      };
    } else {
      return {
        width: '100%',
        height: '100%',
        maxWidth: '300px',
        // maxHeight: '180px',
        border: 'none',
      };
    }
  }

  leftImageIndex: number = 0;
  rightImageIndex: number = 0;

  ngOnInit(): void {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  vm = computed(() => {
    const auction = this.auctionData();
    const swap = this.swapOrder();

    const leftItem = swap ? auction.secondItem : auction.firstItem;
    const rightItem = swap ? auction.firstItem : auction.secondItem;

    const leftImages = swap ? this.secondImages : this.firstImages;
    const rightImages = swap ? this.firstImages : this.secondImages;

    const leftActiveIndex = swap
      ? this.activeIndexSecond
      : this.activeIndexFirst;
    const rightActiveIndex = swap
      ? this.activeIndexFirst
      : this.activeIndexSecond;

    return {
      leftItem,
      rightItem,
      leftImages,
      rightImages,
      leftActiveIndex,
      rightActiveIndex,
      isLeftOriginalFirst: !swap,
      nextAuctionId: auction.nextAuctionId,
      prevAuctionId: auction.prevAuctionId,
    };
  });

  toggleOrder(): void {
    this.swapOrder.update((v) => !v);
  }

  closeMenu(): void {
    this.menuOpen = false;
  }

  sharePage() {
    if (navigator.share) {
      navigator.share({
        title: document.title,
        text: 'Sprawdź tę aukcję 🔥',
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href).then(() => {
        alert('Link skopiowany do schowka 📋');
      });
    }
  }

  isActive(product: ProductItem, isFirstAuctionItem: boolean): boolean {
    return (
      (isFirstAuctionItem && this.activeProduct === 'first') ||
      (!isFirstAuctionItem && this.activeProduct === 'second')
    );
  }

  private calculateFireScale(step: number): number {
    const growthPerStep = 0.2;
    return +(1 + step * growthPerStep).toFixed(2);
  }

  // startCountdown(initial: number) {
  //   this.secondsLeft.set(initial);

  //   this.timerSub?.unsubscribe();
  //   this.timerSub = interval(1000).subscribe(() => {
  //     this.secondsLeft.update((v) => {
  //       const next = v - 1;
  //       return next >= 0 ? next : 0;
  //     });
  //   });
  // }

  startCountdownFromEndAt() {
    this.timerSub?.unsubscribe();

    this.timerSub = interval(1000).subscribe(() => {
      const endAt = this.auctionEndAt();
      if (!endAt) return;

      const now = Date.now();
      const diff = Math.max(0, Math.floor((endAt - now) / 1000));

      this.secondsLeft.set(diff);

      if (diff === 0) {
        this.timerSub.unsubscribe();
        this.onAuctionFinished();
      }
    });
  }
  private onAuctionFinished() {
    this.mercureSub?.unsubscribe();
    this.pollSub?.unsubscribe();

    if (this.token.token) {
      this.router.navigate(['/auction-list']);
    } else {
      this.goToSummaryPage(this.auctionData());
    }
  }

  onTouchStart(event: TouchEvent) {
    this.startX = event.changedTouches[0].screenX;
    this.startY = event.changedTouches[0].screenY;
  }

  onTouchEnd(event: TouchEvent) {
    const endX = event.changedTouches[0].screenX;
    const endY = event.changedTouches[0].screenY;

    const diffX = endX - this.startX;
    const diffY = endY - this.startY;

    const swipeThreshold = window.innerWidth * 0.4;

    if (Math.abs(diffY) > Math.abs(diffX)) return;

    if (diffX > swipeThreshold) {
      this.swipeLeft();
    } else if (diffX < -swipeThreshold) {
      this.swipeRight();
    }
  }

  async swipeLeft() {
    if (!this.token.token) return;

    this.slideLeft = true;
    if (this.vm().prevAuctionId) {
      this.newFirstProduct.set('');
      this.newSecondProduct.set('');
      this.router.navigate(['/auction', this.vm().prevAuctionId]);
      await this.sleep(600);
    } else {
      this.messageService.add({
        severity: 'info',
        summary: `Brak poprzedniej aukcji`,
      });
    }
    this.slideLeft = false;
  }

  async swipeRight() {
    if (!this.token.token) return;

    this.slideRight = true;

    if (this.vm().nextAuctionId) {
      this.newFirstProduct.set('');
      this.newSecondProduct.set('');
      this.router.navigate(['/auction', this.vm().nextAuctionId]);
      await this.sleep(600);
    } else {
      this.messageService.add({
        severity: 'info',
        summary: `Brak kolejnych aukcji`,
      });
    }
    this.slideRight = false;
  }

  sleep(ms: number) {
    return new Promise((res) => setTimeout(res, ms));
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.mercureSub?.unsubscribe();
    this.timerSub?.unsubscribe();
    this.pollSub?.unsubscribe();
  }

  onReserve(
    product: Product,
    isFirstProduct: boolean,
    actuallyStep: number
  ): void {
    if (isFirstProduct) {
      this.firstProductActuallyStep.set(actuallyStep);
      this.newFirstProduct.set(actuallyStep + product.name);
    } else {
      this.secondProductActuallyStep.set(actuallyStep);
      this.newSecondProduct.set(actuallyStep + product.name);
    }

    this.auctionService
      .reserveProduct(
        this.auctionData().id,
        isFirstProduct ? 'first' : 'second',
        actuallyStep
      )
      .subscribe(
        (data) => {
          if (isFirstProduct) {
            this.reservedPositionFirst.set(data.reservationsCount + 1);
          } else {
            this.reservedPositionSecond.set(data.reservationsCount + 1);
          }

          const auction = this.auctionData();
          const levels = isFirstProduct
            ? auction.levels[actuallyStep].firstItem
            : auction.levels[actuallyStep].secondItem;

          const maxStock = levels.stockCount ?? 0;

          if (isFirstProduct) {
            this.maxStockFirst.set(maxStock);
          } else {
            this.maxStockSecond.set(maxStock);
          }

          if (!this.isMobile) {
            this.messageService.add({
              severity: 'success',
              summary: `Pomyślnie zarezerwowano ${product.name}!`,
            });
          }

          if (isFirstProduct) {
            this.firstProductReserved.set(true);
            this.auctionService.firstProductReservations.update((c) => c + 1);
            this.firstProductErrorReserved.set(false);
            this.firstProductErrorMessage.set('');
          } else {
            this.secondProductReserved.set(true);
            this.secondProductErrorMessage.set('');
            this.auctionService.secondProductReservations.update((c) => c + 1);
            this.secondProductErrorReserved.set(false);
          }
        },
        (error) => {
          const auction = this.auctionData();
          const levels = isFirstProduct
            ? auction.levels[actuallyStep].firstItem
            : auction.levels[actuallyStep].secondItem;

          const maxStock = levels.stockCount ?? 0;
          if (isFirstProduct) {
            this.maxStockFirst.set(maxStock);
            this.firstProductErrorReserved.set(true);
            this.firstProductErrorMessage.set(
              error.error?.error || 'Nie udało się tym razem!'
            );
            if (
              error.status === 409 &&
              error.error?.error === 'Rezerwacja już istnieje'
            ) {
              this.auctionService.firstProductReservations.update((c) => c + 1);
            }
          } else {
            this.maxStockSecond.set(maxStock);
            this.secondProductErrorReserved.set(true);
            this.secondProductErrorMessage.set(
              error.error?.error || 'Nie udało się tym razem!'
            );
            if (
              error.status === 409 &&
              error.error?.error === 'Rezerwacja już istnieje'
            ) {
              this.auctionService.secondProductReservations.update((c) => c + 1);
            }
          }

          if (!this.isMobile) {
            this.messageService.add({
              severity: 'error',
              summary: 'Wystąpił błąd przy rezerwacji produktu!',
            });
          }
        }
      );
  }

  private goToSummaryPage(auction: AuctionModel): void {
    this.auctionService.auctionProducts.set(auction);

    this.router.navigate(['auctions', this.auctionId, 'summary'], {
      replaceUrl: true,
    });
  }

  getLeftImages(): any[] {
    return this.vm().isLeftOriginalFirst ? this.firstImages : this.secondImages;
  }

  getCurrentLeftImage(): string {
    const images = this.getLeftImages();
    if (images.length === 0) return '';
    const normalized = this.normalizeImg(images[this.leftImageIndex]);
    return normalized?.src || '';
  }

  getLeftCurrentIndex(): number {
    return this.leftImageIndex;
  }

  getLeftImagesCount(): number {
    return this.getLeftImages().length;
  }

  prevLeftImage(): void {
    const images = this.getLeftImages();
    if (images.length === 0) return;

    this.leftImageIndex =
      this.leftImageIndex === 0 ? images.length - 1 : this.leftImageIndex - 1;
  }

  nextLeftImage(): void {
    const images = this.getLeftImages();
    if (images.length === 0) return;

    this.leftImageIndex =
      this.leftImageIndex === images.length - 1 ? 0 : this.leftImageIndex + 1;
  }

  getRightImages(): any[] {
    return !this.vm().isLeftOriginalFirst
      ? this.firstImages
      : this.secondImages;
  }

  getCurrentRightImage(): string {
    const images = this.getRightImages();
    if (images.length === 0) return '';
    const normalized = this.normalizeImg(images[this.rightImageIndex]);
    return normalized?.src || '';
  }

  getRightCurrentIndex(): number {
    return this.rightImageIndex;
  }

  getRightImagesCount(): number {
    return this.getRightImages().length;
  }

  prevRightImage(): void {
    const images = this.getRightImages();
    if (images.length === 0) return;

    this.rightImageIndex =
      this.rightImageIndex === 0 ? images.length - 1 : this.rightImageIndex - 1;
  }

  nextRightImage(): void {
    const images = this.getRightImages();
    if (images.length === 0) return;

    this.rightImageIndex =
      this.rightImageIndex === images.length - 1 ? 0 : this.rightImageIndex + 1;
  }
}
